import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { useFieldOperationsSettings } from '../context/FieldOperationsSettingsContext';
import {
  captureBrowserGps,
  checkInField,
  checkOutField,
  getMyFieldStatus,
  listFieldAttendanceEvents,
  listFieldLocations,
  listFieldTodayJobs,
  listLiveFieldPresence,
} from '../services/fieldOperationsService';
import type {
  FieldAttendanceEvent,
  FieldCheckInRequest,
  FieldLivePresence,
  FieldLocation,
  FieldTargetKind,
  FieldTodayJob,
} from '../types/FieldOperations';
import styles from './FieldOperationsPage.module.css';

const formatWhen = (value?: string) => {
  if (!value) {
    return '—';
  }

  return formatDateTime(value) || value;
};

const jobKey = (job: FieldTodayJob) => `${job.targetKind}:${job.targetId}`;

const jobLabel = (job: FieldTodayJob) => {
  const when = job.startAtUtc ? `${formatWhen(job.startAtUtc)} — ` : '';
  const place = job.subtitle || job.destination.address;
  return `${when}${job.title}${place ? ` · ${place}` : ''}`;
};

type CheckInMode = 'location' | 'jobs' | 'adHoc';

type Translate = ReturnType<typeof useTranslation>['t'];

// Raw metres get unreadable fast on the geofence columns (e.g. "27523 m").
const formatDistance = (meters: number | null | undefined, t: Translate) => {
  if (meters == null) {
    return '—';
  }

  return meters >= 1000
    ? t('kilometers', { value: (meters / 1000).toFixed(1) })
    : t('meters', { value: Math.round(meters) });
};

export const FieldOperationsPage: React.FC = () => {
  const { t } = useTranslation(['fieldOperations', 'nav', 'common']);
  const { hasPermission } = useAuth();
  const { settings, allowsTarget, loading: settingsLoading } = useFieldOperationsSettings();
  const canCheckIn = hasPermission(PermissionScopes.fieldOperationsCheckIn);
  const canReadAll = hasPermission(PermissionScopes.fieldOperationsRead);
  const canManageSettings = hasPermission(PermissionScopes.fieldOperationsSettings);

  const showsSites = allowsTarget('location');
  const showsAdHoc = allowsTarget('adHoc');
  const showsJobs = allowsTarget('calendarEvent') || allowsTarget('task');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<FieldLocation[]>([]);
  const [todayJobs, setTodayJobs] = useState<FieldTodayJob[]>([]);
  const [openVisit, setOpenVisit] = useState<FieldLivePresence | null>(null);
  const [live, setLive] = useState<FieldLivePresence[]>([]);
  const [events, setEvents] = useState<FieldAttendanceEvent[]>([]);
  const [mode, setMode] = useState<CheckInMode>('location');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedJobKey, setSelectedJobKey] = useState('');
  const [adHocLabel, setAdHocLabel] = useState('');
  const [adHocAddress, setAdHocAddress] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [targetKindFilter, setTargetKindFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [locationRows, jobRows, status, history] = await Promise.all([
        showsSites ? listFieldLocations() : Promise.resolve<FieldLocation[]>([]),
        showsJobs ? listFieldTodayJobs() : Promise.resolve<FieldTodayJob[]>([]),
        getMyFieldStatus(),
        listFieldAttendanceEvents({
          kind: kindFilter || undefined,
          targetKind: targetKindFilter || undefined,
          page: 1,
          pageSize: 50,
        }),
      ]);
      setLocations(locationRows);
      setTodayJobs(jobRows);
      setOpenVisit(status.openVisit ?? null);
      setEvents(history.items);
      setSelectedLocationId((current) => {
        if (current && locationRows.some((location) => location.id === current)) {
          return current;
        }
        return locationRows[0]?.id ?? '';
      });
      setSelectedJobKey((current) => {
        if (current && jobRows.some((job) => jobKey(job) === current)) {
          return current;
        }
        return jobRows[0] ? jobKey(jobRows[0]) : '';
      });

      if (canReadAll) {
        setLive(await listLiveFieldPresence());
      } else {
        setLive([]);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [canReadAll, kindFilter, showsJobs, showsSites, targetKindFilter, t]);

  useEffect(() => {
    if (settingsLoading || !settings.isEnabled) {
      return;
    }

    void load();
  }, [load, settings.isEnabled, settingsLoading]);

  const modeOptions = useMemo<CheckInMode[]>(() => [
    ...(showsSites ? (['location'] as CheckInMode[]) : []),
    ...(showsJobs ? (['jobs'] as CheckInMode[]) : []),
    ...(showsAdHoc ? (['adHoc'] as CheckInMode[]) : []),
  ], [showsAdHoc, showsJobs, showsSites]);

  // Keep the picker on a target the tenant actually allows.
  useEffect(() => {
    if (modeOptions.length === 0 || modeOptions.includes(mode)) {
      return;
    }

    setMode(modeOptions[0]);
  }, [mode, modeOptions]);

  const statusLabel = useMemo(() => {
    if (!openVisit) {
      return t('notCheckedIn');
    }

    return t('checkedInSince', {
      location: openVisit.destination.label || t('unknownDestination'),
      when: formatWhen(openVisit.checkedInAt),
    });
  }, [openVisit, t]);

  const buildCheckInRequest = (): FieldCheckInRequest | string => {
    if (mode === 'location') {
      if (!selectedLocationId) {
        return t('selectLocation');
      }
      return { targetKind: 'location', locationId: selectedLocationId };
    }

    if (mode === 'adHoc') {
      const label = adHocLabel.trim();
      const address = adHocAddress.trim();
      if (!label && !address) {
        return t('adHocNeedsLabel');
      }
      return { targetKind: 'adHoc', label: label || undefined, address: address || undefined };
    }

    const job = todayJobs.find((item) => jobKey(item) === selectedJobKey);
    if (!job) {
      return t('selectJob');
    }

    return job.targetKind === 'task'
      ? { targetKind: 'task', taskId: job.targetId }
      : { targetKind: 'calendarEvent', calendarEventId: job.targetId };
  };

  const submitCheckIn = async () => {
    const request = buildCheckInRequest();
    if (typeof request === 'string') {
      setError(request);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const gps = await captureBrowserGps();
      await checkInField({
        ...request,
        source: 'web',
        latitude: gps?.latitude,
        longitude: gps?.longitude,
        accuracyMeters: gps?.accuracyMeters,
      });
      setAdHocLabel('');
      setAdHocAddress('');
      await load();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToCheckIn'));
    } finally {
      setSaving(false);
    }
  };

  const submitCheckOut = async () => {
    setSaving(true);
    setError(null);
    try {
      const gps = await captureBrowserGps();
      await checkOutField({
        checkInEventId: openVisit?.checkInEventId,
        source: 'web',
        latitude: gps?.latitude,
        longitude: gps?.longitude,
        accuracyMeters: gps?.accuracyMeters,
      });
      await load();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToCheckOut'));
    } finally {
      setSaving(false);
    }
  };

  const availableTargets = (['location', 'calendarEvent', 'task', 'adHoc'] as FieldTargetKind[])
    .filter((kind) => allowsTarget(kind));

  if (settingsLoading) {
    return (
      <div className={styles.page}>
        <Spinner text={t('loading')} />
      </div>
    );
  }

  if (!settings.isEnabled) {
    return (
      <div className={styles.page}>
        <Breadcrumb items={[{ label: t('fieldOperations', { ns: 'nav' }), path: '/field-operations' }]} />
        <section className={styles.sectionCard}>
          <h3 className={styles.sectionTitle}>{t('disabledTitle')}</h3>
          <p className={styles.empty}>
            {canManageSettings ? t('disabledAdminHint') : t('disabledStaffHint')}
          </p>
          {canManageSettings ? (
            <p>
              <Link className="btn btn-primary" to="/field-operations/settings">
                {t('settingsTitle')}
              </Link>
            </p>
          ) : null}
        </section>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={[{ label: t('fieldOperations', { ns: 'nav' }), path: '/field-operations' }]} />
      <div className={styles.headerRow}>
        <div>
          <h2>{t('title')}</h2>
          <p className={styles.pageIntro}>{t('subtitle')}</p>
        </div>
        {canManageSettings ? (
          <Link className="btn btn-secondary" to="/field-operations/settings">
            {t('settingsTitle')}
          </Link>
        ) : null}
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}
      {loading ? <Spinner text={t('loading')} /> : null}

      {!loading && canCheckIn ? (
        <section className={styles.sectionCard}>
          <h3 className={styles.sectionTitle}>{t('myStatus')}</h3>
          <div className={styles.statusRow}>
            <p className={styles.statusText}>
              <span
                className={`${styles.statusDot} ${openVisit ? styles.statusDotActive : ''}`}
                aria-hidden="true"
              />
              {statusLabel}
            </p>
            <div className={styles.actions}>
              {openVisit ? (
                <button type="button" className="btn btn-primary" onClick={() => void submitCheckOut()} disabled={saving}>
                  {saving ? t('saving') : t('checkOut')}
                </button>
              ) : (
                <>
                  {modeOptions.length > 1 ? (
                    <select
                      className={styles.select}
                      value={mode}
                      onChange={(event) => setMode(event.target.value as CheckInMode)}
                      disabled={saving}
                      aria-label={t('checkInTarget')}
                    >
                      {modeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option === 'jobs' ? t('targetGroup_jobs') : t(`target_${option}`)}
                        </option>
                      ))}
                    </select>
                  ) : null}

                  {mode === 'location' ? (
                    <select
                      className={styles.select}
                      value={selectedLocationId}
                      onChange={(event) => setSelectedLocationId(event.target.value)}
                      disabled={saving}
                      aria-label={t('location')}
                    >
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  ) : null}

                  {mode === 'jobs' ? (
                    <select
                      className={styles.select}
                      value={selectedJobKey}
                      onChange={(event) => setSelectedJobKey(event.target.value)}
                      disabled={saving || todayJobs.length === 0}
                      aria-label={t('todayJobs')}
                    >
                      {todayJobs.length === 0 ? <option value="">{t('noJobsToday')}</option> : null}
                      {todayJobs.map((job) => (
                        <option key={jobKey(job)} value={jobKey(job)}>
                          {jobLabel(job)}
                        </option>
                      ))}
                    </select>
                  ) : null}

                  {mode === 'adHoc' ? (
                    <>
                      <input
                        className={styles.select}
                        value={adHocLabel}
                        onChange={(event) => setAdHocLabel(event.target.value)}
                        placeholder={t('adHocLabel')}
                        disabled={saving}
                        aria-label={t('adHocLabel')}
                      />
                      <input
                        className={styles.select}
                        value={adHocAddress}
                        onChange={(event) => setAdHocAddress(event.target.value)}
                        placeholder={t('adHocAddress')}
                        disabled={saving}
                        aria-label={t('adHocAddress')}
                      />
                    </>
                  ) : null}

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void submitCheckIn()}
                    disabled={saving || (mode === 'location' && locations.length === 0)}
                  >
                    {saving ? t('saving') : t('checkIn')}
                  </button>
                </>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {!loading && canReadAll ? (
        <section className={styles.sectionCard}>
          <h3 className={styles.sectionTitle}>{t('liveBoard')}</h3>
          {live.length === 0 ? (
            <p className={styles.empty}>{t('nobodyCheckedIn')}</p>
          ) : (
            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>{t('employee')}</th>
                    <th>{t('destination')}</th>
                    <th>{t('target')}</th>
                    <th>{t('checkedInAt')}</th>
                    <th>{t('source')}</th>
                    <th>{t('deviceId')}</th>
                    <th>{t('distance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {live.map((row) => (
                    <tr key={`${row.employeeId}-${row.checkInEventId}`}>
                      <td className={styles.cellStrong}>{row.employeeName}</td>
                      <td>{row.destination.label || '—'}</td>
                      <td>
                        <span className={styles.badge}>
                          {t(`target_${row.destination.targetKind}`, { defaultValue: row.destination.targetKind })}
                        </span>
                      </td>
                      <td className={styles.cellNumeric}>{formatWhen(row.checkedInAt)}</td>
                      <td>{t(`source_${row.source}`, { defaultValue: row.source })}</td>
                      <td className={styles.cellNumeric}>{row.deviceId || '—'}</td>
                      <td className={styles.cellNumeric}>{formatDistance(row.distanceMeters, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {!loading ? (
        <section className={styles.sectionCard}>
          <h3 className={styles.sectionTitle}>{t('history')}</h3>
          <div className={styles.filters}>
            <label className={styles.filterField}>
              {t('kind')}
              <select className={styles.control} value={kindFilter} onChange={(event) => setKindFilter(event.target.value)}>
                <option value="">{t('allKinds')}</option>
                <option value="checkIn">{t('kind_checkIn')}</option>
                <option value="checkOut">{t('kind_checkOut')}</option>
              </select>
            </label>
            <label className={styles.filterField}>
              {t('target')}
              <select
                className={styles.control}
                value={targetKindFilter}
                onChange={(event) => setTargetKindFilter(event.target.value)}
              >
                <option value="">{t('allTargets')}</option>
                {availableTargets.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`target_${kind}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {events.length === 0 ? (
            <p className={styles.empty}>{t('noEvents')}</p>
          ) : (
            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>{t('when')}</th>
                    <th>{t('employee')}</th>
                    <th>{t('destination')}</th>
                    <th>{t('target')}</th>
                    <th>{t('kind')}</th>
                    <th>{t('source')}</th>
                    <th>{t('deviceId')}</th>
                    <th>{t('distance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((row) => (
                    <tr key={row.id}>
                      <td className={styles.cellNumeric}>{formatWhen(row.occurredAt)}</td>
                      <td className={styles.cellStrong}>{row.employeeName}</td>
                      <td>{row.destination.label || '—'}</td>
                      <td>
                        <span className={styles.badge}>
                          {t(`target_${row.destination.targetKind}`, { defaultValue: row.destination.targetKind })}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`${styles.badge} ${row.kind === 'checkIn' ? styles.badgeCheckIn : styles.badgeCheckOut}`}
                        >
                          {t(`kind_${row.kind}`, { defaultValue: row.kind })}
                        </span>
                      </td>
                      <td>{t(`source_${row.source}`, { defaultValue: row.source })}</td>
                      <td className={styles.cellNumeric}>{row.deviceId || '—'}</td>
                      <td className={styles.cellNumeric}>{formatDistance(row.distanceMeters, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
};
